import React, { useState, useEffect, useCallback, useMemo } from 'react';
import PageHeader from '../../components/common/PageHeader';
import ConfirmationModal from '../../components/common/ConfirmationModal';
import { StructureEditorModal } from '../../components/chemistry/StructureEditor';
import StructureImage from '../../components/chemistry/StructureImage';
import apiClient from '../../api/client';
import { useAuth } from '../../context/AuthContext';
import {
  registrationAPI,
  RegisteredCompound,
  AuditEntry,
  StandardiseResult,
  UniquenessResult,
  RegistrationRules,
  RegistrationStats,
  Salt,
  TherapeuticArea,
} from '../../services/registrationApi';
import { formatDate, formatDateTime } from '../../utils/dateFormat';

// =====================================================================
// Tab types
// =====================================================================

type RegistryTab = 'compounds' | 'versions' | 'lots' | 'salts' | 'therapeutic-areas' | 'register' | 'audit';

// =====================================================================
// Small helper: render structure SVG via Indigo
// =====================================================================

const useStructureSvg = (smiles: string | null, width = '200px', height = '100px') => {
  const [svg, setSvg] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!smiles) { setSvg(null); return; }
    let cancelled = false;
    setLoading(true);
    apiClient.post('/v2/indigo/render', {
      structure: { value: smiles, type: 'RAW' },
      width,
      height,
    }).then(r => {
      if (!cancelled) {
        const data = typeof r.data === 'string' ? r.data : r.data.svg || r.data;
        setSvg(data);
      }
    }).catch(() => { if (!cancelled) setSvg(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [smiles, width, height]);

  return { svg, loading };
};

// =====================================================================
// Copy button helper
// =====================================================================

const CopyButton: React.FC<{ text: string }> = ({ text }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <button
      onClick={handleCopy}
      className="ml-1 p-0.5 rounded transition-colors"
      style={{ color: copied ? '#0D9488' : 'var(--text-muted)' }}
      title="Copy to clipboard"
    >
      {copied ? (
        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
      ) : (
        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <rect x="9" y="9" width="13" height="13" rx="2" ry="2" strokeWidth={2} />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
        </svg>
      )}
    </button>
  );
};

// =====================================================================
// Inline structure thumbnail for compound table rows
// =====================================================================

const StructureThumbnail: React.FC<{ smiles: string }> = ({ smiles }) => {
  const { svg, loading } = useStructureSvg(smiles, '120px', '60px');
  if (loading) return <div className="w-4 h-4 border-2 border-gray-300 border-t-teal-500 rounded-full animate-spin" />;
  if (!svg) return <span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>-</span>;
  return <div dangerouslySetInnerHTML={{ __html: svg }} style={{ maxWidth: 120, maxHeight: 60 }} />;
};

// =====================================================================
// Status badge
// =====================================================================

const STATUS_COLORS: Record<string, { bg: string; text: string; border: string }> = {
  registered:   { bg: '#f0fdf4', text: '#166534', border: '#bbf7d0' },
  candidate:    { bg: '#eff6ff', text: '#1e40af', border: '#bfdbfe' },
  preclinical:  { bg: '#fefce8', text: '#854d0e', border: '#fef08a' },
  development:  { bg: '#fff7ed', text: '#9a3412', border: '#fed7aa' },
  clinical:     { bg: '#faf5ff', text: '#7c3aed', border: '#ddd6fe' },
  approved:     { bg: '#ecfdf5', text: '#065f46', border: '#a7f3d0' },
  suspended:    { bg: '#fef2f2', text: '#991b1b', border: '#fecaca' },
  withdrawn:    { bg: 'var(--content-bg)', text: 'var(--text-secondary)', border: 'var(--card-border)' },
  archived:     { bg: 'var(--content-bg)', text: 'var(--text-secondary)', border: 'var(--card-border)' },
  deleted:      { bg: '#fef2f2', text: '#7f1d1d', border: '#fca5a5' },
};

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const colors = STATUS_COLORS[status] || STATUS_COLORS.registered;
  return (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium capitalize"
      style={{ backgroundColor: colors.bg, color: colors.text, border: `1px solid ${colors.border}` }}
    >
      {status}
    </span>
  );
};

// =====================================================================
// Stats Bar
// =====================================================================

const StatsBar: React.FC<{ stats: RegistrationStats | null; loading: boolean; saltsCount: number; taCount: number }> = ({ stats, loading, saltsCount, taCount }) => {
  const items = stats ? [
    { label: 'Parents', value: stats.total_parents },
    { label: 'Versions', value: stats.total_versions },
    { label: 'Lots', value: stats.total_lots },
    { label: 'Salts', value: saltsCount },
    { label: 'Therapeutic Areas', value: taCount },
  ] : [];

  return (
    <div className="flex items-center gap-4 px-4 py-3 border-b" style={{ borderColor: 'var(--card-border)', backgroundColor: 'var(--card-bg)' }}>
      {loading ? (
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 border-2 border-gray-300 border-t-teal-500 rounded-full animate-spin" />
          <span className="text-xs" style={{ color: 'var(--text-muted)' }}>Loading stats...</span>
        </div>
      ) : items.map((item) => (
        <div key={item.label} className="flex items-center gap-1.5">
          <span className="text-lg font-bold font-mono" style={{ color: 'var(--text-primary)' }}>{item.value}</span>
          <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>{item.label}</span>
        </div>
      ))}
    </div>
  );
};

// =====================================================================
// Compounds Tab
// =====================================================================

const CompoundsTab: React.FC = () => {
  const [compounds, setCompounds] = useState<RegisteredCompound[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState<RegistrationStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [expandedCode, setExpandedCode] = useState<string | null>(null);
  const [expandedCompound, setExpandedCompound] = useState<RegisteredCompound | null>(null);
  const [selectedCodes, setSelectedCodes] = useState<Set<string>>(new Set());
  const [expandLoading, setExpandLoading] = useState(false);
  const [saltsCount, setSaltsCount] = useState(0);
  const [taCount, setTaCount] = useState(0);
  const [taFilter, setTaFilter] = useState('');
  const [therapeuticAreas, setTherapeuticAreas] = useState<TherapeuticArea[]>([]);
  const limit = 50;
  const offsetRef = React.useRef(0);

  const fetchCompounds = useCallback(async (append = false) => {
    if (append) {
      setLoadingMore(true);
    } else {
      setLoading(true);
      offsetRef.current = 0;
    }
    setError(null);
    try {
      const r = await registrationAPI.getCompounds({
        limit,
        offset: offsetRef.current,
        status: statusFilter || undefined,
        search: search || undefined,
        therapeutic_area: taFilter || undefined,
      });
      const data = r.data;
      const items = Array.isArray(data) ? data : [];
      items.sort((a: any, b: any) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
      if (append) {
        setCompounds(prev => [...prev, ...items]);
      } else {
        setCompounds(items);
      }
      offsetRef.current += items.length;
      setHasMore(items.length >= limit);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load compounds');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [statusFilter, search, taFilter]);

  const fetchStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const [statsR, saltsR, taR] = await Promise.all([
        registrationAPI.getStats(),
        registrationAPI.getSaltDictionary(),
        registrationAPI.getTherapeuticAreas(),
      ]);
      setStats(statsR.data);
      const saltsData = saltsR.data;
      setSaltsCount(Array.isArray(saltsData) ? saltsData.length : 0);
      const taData = taR.data;
      const taList = Array.isArray(taData) ? taData : [];
      setTaCount(taList.length);
      setTherapeuticAreas(taList);
    } catch {
      // Stats are non-critical
    } finally {
      setStatsLoading(false);
    }
  }, []);

  useEffect(() => { fetchCompounds(false); }, [fetchCompounds]);
  useEffect(() => { fetchStats(); }, [fetchStats]);

  const handleLoadMore = () => {
    fetchCompounds(true);
  };

  const handleExpand = async (parentCode: string) => {
    if (expandedCode === parentCode) {
      setExpandedCode(null);
      setExpandedCompound(null);
      return;
    }
    setExpandedCode(parentCode);
    setExpandLoading(true);
    try {
      const r = await registrationAPI.getCompound(parentCode);
      setExpandedCompound(r.data);
    } catch {
      setExpandedCompound(null);
    } finally {
      setExpandLoading(false);
    }
  };

  // Confirmation modal state
  const [confirmAction, setConfirmAction] = useState<{
    type: 'withdraw' | 'reinstate' | 'promote' | 'demote' | 'permanent-delete';
    code: string;
  } | null>(null);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [confirmError, setConfirmError] = useState<string | null>(null);

  const CONFIRM_CONFIG = {
    withdraw:  { title: (c: string) => `Withdraw ${c}?`, desc: 'This will soft-delete the compound. It can be reinstated later.', label: 'Withdraw', color: 'red' as const },
    reinstate: { title: (c: string) => `Reinstate ${c}?`, desc: 'This will restore the compound to active status.', label: 'Reinstate', color: 'green' as const },
    promote:   { title: (c: string) => `Promote ${c} to Development?`, desc: 'The compound will be promoted to development status.', label: 'Promote', color: 'blue' as const },
    demote:    { title: (c: string) => `Demote ${c}?`, desc: 'This will revert the compound to registered status.', label: 'Demote', color: 'amber' as const },
    'permanent-delete': { title: (c: string) => `Permanently Delete ${c}?`, desc: 'This will PERMANENTLY remove this compound. This action CANNOT be undone. The DIS code will not be reused.', label: 'Permanently Delete', color: 'red' as const },
  };

  const handleConfirm = useCallback(async (reason?: string) => {
    if (!confirmAction || !reason?.trim()) return;
    setConfirmLoading(true);
    setConfirmError(null);
    try {
      const { type, code } = confirmAction;
      if (type === 'withdraw')  await registrationAPI.withdraw(code, reason.trim());
      if (type === 'reinstate') await registrationAPI.reinstate(code, reason.trim());
      if (type === 'promote')   await registrationAPI.promote(code, reason.trim());
      if (type === 'demote')    await registrationAPI.demote(code, reason.trim());
      if (type === 'permanent-delete') await registrationAPI.permanentDelete(code, reason.trim());
      setConfirmAction(null);
      fetchCompounds();
      fetchStats();
    } catch (err: any) {
      setConfirmError(err.response?.data?.detail || `Failed to ${confirmAction.type}`);
    } finally {
      setConfirmLoading(false);
    }
  }, [confirmAction, fetchCompounds, fetchStats]);

  // Bulk action modal
  const [bulkAction, setBulkAction] = useState<{ type: 'bulk-withdraw' | 'bulk-promote' } | null>(null);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkError, setBulkError] = useState<string | null>(null);

  const handleBulkConfirm = useCallback(async (reason?: string) => {
    if (!bulkAction || !reason?.trim() || selectedCodes.size === 0) return;
    setBulkLoading(true);
    setBulkError(null);
    try {
      const codes = Array.from(selectedCodes);
      if (bulkAction.type === 'bulk-withdraw') {
        await registrationAPI.bulkWithdraw(codes, reason.trim());
      } else {
        await registrationAPI.bulkPromote(codes, reason.trim());
      }
      setBulkAction(null);
      setSelectedCodes(new Set());
      fetchCompounds();
      fetchStats();
    } catch (err: any) {
      setBulkError(err.response?.data?.detail || `Bulk ${bulkAction.type} failed`);
    } finally {
      setBulkLoading(false);
    }
  }, [bulkAction, selectedCodes, fetchCompounds, fetchStats]);

  const toggleSelect = (code: string) => {
    setSelectedCodes(prev => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code); else next.add(code);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedCodes.size === compounds.length) {
      setSelectedCodes(new Set());
    } else {
      setSelectedCodes(new Set(compounds.map(c => c.parent_code)));
    }
  };

  const totalParents = useMemo(() => {
    if (!stats) return compounds.length;
    if (statusFilter === 'all') return stats.total_parents;
    if (statusFilter === '') {
      // "Active" = total minus withdrawn minus deleted
      const wd = (stats.by_status?.['withdrawn'] ?? 0) + (stats.by_status?.['deleted'] ?? 0);
      return stats.total_parents - wd;
    }
    return stats.by_status?.[statusFilter] ?? compounds.length;
  }, [stats, statusFilter, compounds.length]);

  return (
    <div className="flex flex-col h-full">
      <StatsBar stats={stats} loading={statsLoading} saltsCount={saltsCount} taCount={taCount} />

      {/* Search / filter bar */}
      <div className="flex items-center gap-3 px-4 py-3 border-b" style={{ borderColor: 'var(--card-border)' }}>
        <div className="relative flex-1" style={{ minWidth: 200, maxWidth: 448 }}>
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: 'var(--text-muted)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            value={search}
            onChange={(e) => { setSearch(e.target.value); }}
            placeholder="Search by code, SMILES, or formula..."
            className="w-full pl-9 pr-3 py-1.5 text-sm rounded-lg border transition-colors"
            style={{
              backgroundColor: 'var(--input-bg)',
              borderColor: 'var(--input-border)',
              color: 'var(--text-primary)',
            }}
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); }}
          className="px-3 py-1.5 text-sm rounded-lg border transition-colors"
          style={{
            backgroundColor: 'var(--input-bg)',
            borderColor: 'var(--input-border)',
            color: 'var(--text-primary)',
          }}
        >
          <option value="all">All Compounds</option>
          <option value="">Active (excl. withdrawn)</option>
          <option value="withdrawn">Withdrawn only</option>
          <option value="registered">Registered</option>
          <option value="candidate">Candidate</option>
          <option value="development">Development</option>
          <option value="clinical">Clinical</option>
          <option value="approved">Approved</option>
        </select>
        <select
          value={taFilter}
          onChange={(e) => { setTaFilter(e.target.value); }}
          className="px-3 py-1.5 text-sm rounded-lg border transition-colors"
          style={{
            backgroundColor: 'var(--input-bg)',
            borderColor: 'var(--input-border)',
            color: 'var(--text-primary)',
          }}
        >
          <option value="">All Areas</option>
          {therapeuticAreas.map(ta => (
            <option key={ta.code} value={ta.code}>{ta.code} - {ta.name}</option>
          ))}
        </select>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-6 h-6 border-2 border-gray-300 border-t-teal-500 rounded-full animate-spin" />
          </div>
        ) : error ? (
          <div className="flex items-center justify-center py-20">
            <span className="text-sm" style={{ color: '#ef4444' }}>{error}</span>
          </div>
        ) : compounds.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 gap-2">
            <svg className="w-12 h-12" style={{ color: 'var(--text-muted)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.75 3.104v5.714a2.25 2.25 0 01-.659 1.591L5 14.5M9.75 3.104c-.251.023-.501.05-.75.082m.75-.082a24.301 24.301 0 014.5 0m0 0v5.714c0 .597.237 1.17.659 1.591L19.8 15.3M14.25 3.104c.251.023.501.05.75.082M19.8 15.3l-1.57.393A9.065 9.065 0 0112 15a9.065 9.065 0 00-6.23.693L5 14.5m14.8.8l1.402 1.402c1.232 1.232.65 3.318-1.067 3.611A48.309 48.309 0 0112 21c-2.773 0-5.491-.235-8.135-.687-1.718-.293-2.3-2.379-1.067-3.61L5 14.5" />
            </svg>
            <span className="text-sm" style={{ color: 'var(--text-muted)' }}>No registered compounds found</span>
          </div>
        ) : (
          <>
          {/* Bulk action bar */}
          {selectedCodes.size > 0 && (
            <div className="sticky top-0 z-10 flex items-center gap-3 px-4 py-2 border-b" style={{ backgroundColor: 'var(--card-bg)', borderColor: 'var(--card-border)' }}>
              <span className="text-xs font-medium" style={{ color: 'var(--text-primary)' }}>{selectedCodes.size} selected</span>
              <button
                onClick={() => setBulkAction({ type: 'bulk-withdraw' })}
                className="px-3 py-1 text-xs font-medium rounded-lg border transition-colors hover:bg-red-50"
                style={{ color: '#991b1b', borderColor: '#fecaca' }}
              >
                Withdraw Selected
              </button>
              <button
                onClick={() => setBulkAction({ type: 'bulk-promote' })}
                className="px-3 py-1 text-xs font-medium rounded-lg border transition-colors hover:bg-blue-50"
                style={{ color: '#1e40af', borderColor: '#bfdbfe' }}
              >
                Promote Selected
              </button>
              <button
                onClick={() => setSelectedCodes(new Set())}
                className="px-3 py-1 text-xs rounded-lg transition-colors"
                style={{ color: 'var(--text-muted)' }}
              >
                Clear
              </button>
            </div>
          )}

          <table className="w-full text-sm" style={{ color: 'var(--text-primary)' }}>
            <thead>
              <tr className="border-b" style={{ borderColor: 'var(--card-border)', backgroundColor: 'var(--hover-bg)' }}>
                <th className="w-10 px-2 py-2">
                  <input type="checkbox" checked={selectedCodes.size > 0 && selectedCodes.size === compounds.length} onChange={toggleSelectAll} className="rounded border-gray-300 text-teal-600" />
                </th>
                <th className="text-left px-4 py-2 text-xs font-medium" style={{ color: 'var(--text-secondary)' }}>Parent Code</th>
                <th className="text-left px-4 py-2 text-xs font-medium" style={{ color: 'var(--text-secondary)' }}>Structure</th>
                <th className="text-left px-4 py-2 text-xs font-medium" style={{ color: 'var(--text-secondary)' }}>Formula</th>
                <th className="text-right px-4 py-2 text-xs font-medium" style={{ color: 'var(--text-secondary)' }}>MW</th>
                <th className="text-left px-4 py-2 text-xs font-medium" style={{ color: 'var(--text-secondary)' }}>Status</th>
                <th className="text-left px-4 py-2 text-xs font-medium" style={{ color: 'var(--text-secondary)' }}>Registered By</th>
                <th className="text-left px-4 py-2 text-xs font-medium" style={{ color: 'var(--text-secondary)' }}>Therapeutic Area</th>
                <th className="text-left px-4 py-2 text-xs font-medium" style={{ color: 'var(--text-secondary)' }}>
                  Created
                  <svg className="inline-block w-3 h-3 ml-0.5 -mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: '#0D9488' }}>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </th>
                <th className="text-center px-2 py-2 text-xs font-medium" style={{ color: 'var(--text-secondary)' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {compounds.map((c) => (
                <React.Fragment key={c.parent_code}>
                  <tr
                    className="border-b cursor-pointer transition-colors"
                    style={{
                      borderColor: 'var(--card-border)',
                      opacity: c.status === 'withdrawn' ? 0.5 : 1,
                      textDecoration: c.status === 'withdrawn' ? 'line-through' : 'none',
                      backgroundColor: selectedCodes.has(c.parent_code) ? 'rgba(13, 148, 136, 0.05)' : undefined,
                    }}
                    onClick={() => handleExpand(c.parent_code)}
                    onMouseEnter={(e) => { if (!selectedCodes.has(c.parent_code)) e.currentTarget.style.backgroundColor = 'var(--hover-bg)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = selectedCodes.has(c.parent_code) ? 'rgba(13, 148, 136, 0.05)' : expandedCode === c.parent_code ? 'var(--hover-bg)' : ''; }}
                  >
                    <td className="w-10 px-2 py-2" onClick={(e) => e.stopPropagation()}>
                      <input type="checkbox" checked={selectedCodes.has(c.parent_code)} onChange={() => toggleSelect(c.parent_code)} className="rounded border-gray-300 text-teal-600" />
                    </td>
                    <td className="px-4 py-2 font-mono font-medium text-xs" style={{ color: '#0D9488' }}>
                      <span className="flex items-center gap-1">
                        <svg
                          className="w-3 h-3 transition-transform"
                          style={{ transform: expandedCode === c.parent_code ? 'rotate(90deg)' : 'rotate(0deg)', color: 'var(--text-muted)' }}
                          fill="none" stroke="currentColor" viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                        {c.parent_code}
                        {(c as any).inn_name && (
                          <span className="ml-1.5 text-[10px] italic" style={{ color: 'var(--text-secondary)' }}>
                            {(c as any).inn_name}
                          </span>
                        )}
                        {(c as any).clinical_phase && (
                          <span className="ml-1 text-[10px] font-medium px-1.5 py-0.5 rounded" style={{ backgroundColor: '#faf5ff', color: '#7c3aed' }}>
                            {(c as any).clinical_phase.replace('_', ' ')}
                          </span>
                        )}
                      </span>
                    </td>
                    <td className="px-4 py-1"><StructureThumbnail smiles={c.canonical_smiles} /></td>
                    <td className="px-4 py-2 text-xs">{c.mol_formula || '-'}</td>
                    <td className="px-4 py-2 text-xs text-right font-mono">{c.mol_weight ? Math.round(c.mol_weight * 100) / 100 : '-'}</td>
                    <td className="px-4 py-2"><StatusBadge status={c.status} /></td>
                    <td className="px-4 py-2 text-xs" style={{ color: 'var(--text-secondary)' }}>{c.created_by_name || '-'}</td>
                    <td className="px-4 py-2 text-xs">{c.therapeutic_area_name || c.therapeutic_area || '-'}</td>
                    <td className="px-4 py-2 text-xs" style={{ color: 'var(--text-secondary)' }}>
                      {formatDate(c.created_at)}
                    </td>
                    <td className="px-2 py-2 text-center">
                      <div className="flex items-center justify-center gap-1">
                        {c.status === 'withdrawn' || c.status === 'deleted' ? (
                          <>
                            <button
                              onClick={(e) => { e.stopPropagation(); setConfirmAction({ type: 'reinstate', code: c.parent_code }); }}
                              className="text-xs px-2 py-0.5 rounded border transition-colors hover:bg-emerald-50"
                              style={{ color: '#065f46', borderColor: '#a7f3d0' }}
                              title="Reinstate compound"
                            >
                              Reinstate
                            </button>
                            <button
                              onClick={(e) => { e.stopPropagation(); setConfirmAction({ type: 'permanent-delete', code: c.parent_code }); }}
                              className="text-xs px-1.5 py-0.5 rounded border transition-colors hover:bg-red-50"
                              style={{ color: '#dc2626', borderColor: '#fecaca', fontSize: '10px' }}
                              title="Permanently delete (cannot be undone)"
                            >
                              Delete
                            </button>
                          </>
                        ) : c.status === 'development' ? (
                          <button
                            onClick={(e) => { e.stopPropagation(); setConfirmAction({ type: 'demote', code: c.parent_code }); }}
                            className="text-xs px-2 py-0.5 rounded border transition-colors hover:bg-amber-50"
                            style={{ color: '#92400e', borderColor: '#fde68a' }}
                            title="Demote to registered (Super Admin)"
                          >
                            Demote
                          </button>
                        ) : (
                          <>
                            <button
                              onClick={(e) => { e.stopPropagation(); setConfirmAction({ type: 'promote', code: c.parent_code }); }}
                              className="text-xs px-2 py-0.5 rounded border transition-colors hover:bg-blue-50"
                              style={{ color: '#1e40af', borderColor: '#bfdbfe' }}
                              title="Promote to development"
                            >
                              Promote
                            </button>
                            <button
                              onClick={(e) => { e.stopPropagation(); setConfirmAction({ type: 'withdraw', code: c.parent_code }); }}
                              className="text-xs px-2 py-0.5 rounded border transition-colors hover:bg-red-50"
                              style={{ color: '#991b1b', borderColor: '#fecaca' }}
                              title="Withdraw compound"
                            >
                              Withdraw
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                  {/* Expanded version/lot tree */}
                  {expandedCode === c.parent_code && (
                    <tr>
                      <td colSpan={10} className="px-4 py-3" style={{ backgroundColor: 'var(--hover-bg)' }}>
                        {expandLoading ? (
                          <div className="flex items-center gap-2 pl-6">
                            <div className="w-4 h-4 border-2 border-gray-300 border-t-teal-500 rounded-full animate-spin" />
                            <span className="text-xs" style={{ color: 'var(--text-muted)' }}>Loading versions...</span>
                          </div>
                        ) : expandedCompound?.versions && expandedCompound.versions.length > 0 ? (
                          <div className="pl-6 space-y-2">
                            {expandedCompound.versions.map((v) => (
                              <div key={v.version_code} className="rounded-lg border p-3" style={{ borderColor: 'var(--card-border)', backgroundColor: 'var(--card-bg)' }}>
                                <div className="flex items-center gap-3 mb-1">
                                  <span className="font-mono text-xs font-medium" style={{ color: '#0D9488' }}>{v.version_code}</span>
                                  {v.salt_name && (
                                    <span className="text-xs px-1.5 py-0.5 rounded" style={{ backgroundColor: '#fef3c7', color: '#92400e' }}>
                                      Salt: {v.salt_name}
                                    </span>
                                  )}
                                  {v.is_candidate && (
                                    <span className="text-xs px-1.5 py-0.5 rounded" style={{ backgroundColor: '#dbeafe', color: '#1e40af' }}>
                                      Candidate
                                    </span>
                                  )}
                                  {v.supplier && <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>Supplier: {v.supplier}</span>}
                                </div>
                                {v.lots.length > 0 ? (
                                  <div className="mt-2 space-y-1">
                                    {v.lots.map((lot) => (
                                      <div
                                        key={lot.lot_code}
                                        className="flex items-center gap-4 pl-4 py-1 text-xs border-l-2"
                                        style={{ borderColor: '#0D9488' }}
                                      >
                                        <span className="font-mono" style={{ color: 'var(--text-primary)' }}>{lot.lot_code}</span>
                                        {lot.batch_number && <span style={{ color: 'var(--text-secondary)' }}>Batch: {lot.batch_number}</span>}
                                        {lot.purity_percent != null && <span style={{ color: 'var(--text-secondary)' }}>Purity: {lot.purity_percent}%</span>}
                                        {lot.ee_percent != null && <span style={{ color: 'var(--text-secondary)' }}>ee: {lot.ee_percent}%</span>}
                                        <span style={{ color: 'var(--text-muted)' }}>{formatDate(lot.created_at)}</span>
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <span className="text-xs pl-4" style={{ color: 'var(--text-muted)' }}>No lots recorded</span>
                                )}
                              </div>
                            ))}
                          </div>
                        ) : (
                          <span className="text-xs pl-6" style={{ color: 'var(--text-muted)' }}>No versions found</span>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
          </>
        )}
      </div>

      {/* Load More + count */}
      <div className="flex items-center justify-between px-4 py-3 border-t" style={{ borderColor: 'var(--card-border)' }}>
        <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>
          {hasMore
            ? `Showing ${compounds.length} of ${totalParents} compounds`
            : `Showing all ${compounds.length} compounds`}
        </span>
        {hasMore && !loading && (
          <button
            onClick={handleLoadMore}
            disabled={loadingMore}
            className="px-3 py-1.5 text-xs font-medium rounded-lg transition-colors disabled:opacity-50"
            style={{ backgroundColor: '#0D9488', color: 'white' }}
          >
            {loadingMore ? 'Loading...' : `Load More (${totalParents - compounds.length} remaining)`}
          </button>
        )}
      </div>

      {/* Confirmation modal for single compound actions */}
      {confirmAction && (
        <ConfirmationModal
          isOpen={!!confirmAction}
          onClose={() => { setConfirmAction(null); setConfirmError(null); }}
          onConfirm={handleConfirm}
          title={CONFIRM_CONFIG[confirmAction.type].title(confirmAction.code)}
          description={CONFIRM_CONFIG[confirmAction.type].desc}
          confirmLabel={CONFIRM_CONFIG[confirmAction.type].label}
          confirmColor={CONFIRM_CONFIG[confirmAction.type].color}
          requireReason
          loading={confirmLoading}
          error={confirmError}
        />
      )}

      {/* Bulk action modal */}
      {bulkAction && (
        <ConfirmationModal
          isOpen={!!bulkAction}
          onClose={() => { setBulkAction(null); setBulkError(null); }}
          onConfirm={handleBulkConfirm}
          title={bulkAction.type === 'bulk-withdraw'
            ? `Withdraw ${selectedCodes.size} compounds?`
            : `Promote ${selectedCodes.size} compounds to Development?`}
          description={bulkAction.type === 'bulk-withdraw'
            ? 'This will soft-delete the selected compounds. They can be reinstated later.'
            : 'The selected compounds will be promoted to development status.'}
          confirmLabel={bulkAction.type === 'bulk-withdraw'
            ? `Withdraw All (${selectedCodes.size})`
            : `Promote All (${selectedCodes.size})`}
          confirmColor={bulkAction.type === 'bulk-withdraw' ? 'red' : 'blue'}
          requireReason
          loading={bulkLoading}
          error={bulkError}
        />
      )}
    </div>
  );
};

// =====================================================================
// Register Tab
// =====================================================================

const RegisterTab: React.FC = () => {
  const [smiles, setSmiles] = useState('');
  const [standardising, setStandardising] = useState(false);
  const [stdResult, setStdResult] = useState<StandardiseResult | null>(null);
  const [stdError, setStdError] = useState<string | null>(null);
  const [checking, setChecking] = useState(false);
  const [uniquenessResult, setUniquenessResult] = useState<UniquenessResult | null>(null);
  const [uniquenessError, setUniquenessError] = useState<string | null>(null);
  const [therapeuticAreas, setTherapeuticAreas] = useState<TherapeuticArea[]>([]);
  const [salts, setSalts] = useState<Salt[]>([]);
  const [selectedTA, setSelectedTA] = useState<string | undefined>(undefined);
  const [selectedSalt, setSelectedSalt] = useState<string>('');
  const [notes, setNotes] = useState('');
  const [registering, setRegistering] = useState(false);
  const [registerResult, setRegisterResult] = useState<{ parent_code: string; version_code: string; lot_code: string; message: string } | null>(null);
  const [registerError, setRegisterError] = useState<string | null>(null);
  const [showKetcher, setShowKetcher] = useState(false);

  const { svg: structureSvg, loading: svgLoading } = useStructureSvg(stdResult?.canonical_smiles || null, '360px', '180px');

  useEffect(() => {
    registrationAPI.getTherapeuticAreas()
      .then(r => setTherapeuticAreas(Array.isArray(r.data) ? r.data : []))
      .catch(() => {});
    registrationAPI.getSaltDictionary()
      .then(r => setSalts(Array.isArray(r.data) ? r.data.filter((s: Salt) => s.enabled) : []))
      .catch(() => {});
  }, []);

  const handleStandardise = async () => {
    if (!smiles.trim()) return;
    setStandardising(true);
    setStdError(null);
    setStdResult(null);
    setUniquenessResult(null);
    setRegisterResult(null);
    setRegisterError(null);
    try {
      const r = await registrationAPI.standardise({ smiles: smiles.trim() });
      setStdResult(r.data);
    } catch (err: any) {
      setStdError(err.response?.data?.detail || 'Standardisation failed');
    } finally {
      setStandardising(false);
    }
  };

  const handleCheckUniqueness = async () => {
    if (!stdResult?.inchi_key) return;
    setChecking(true);
    setUniquenessError(null);
    setUniquenessResult(null);
    try {
      const r = await registrationAPI.checkUniqueness({ smiles: stdResult.canonical_smiles });
      setUniquenessResult(r.data);
    } catch (err: any) {
      setUniquenessError(err.response?.data?.detail || 'Uniqueness check failed');
    } finally {
      setChecking(false);
    }
  };

  const handleRegister = async () => {
    if (!smiles.trim()) return;
    setRegistering(true);
    setRegisterError(null);
    setRegisterResult(null);
    try {
      const r = await registrationAPI.register({
        smiles: smiles.trim(),
        therapeutic_area: selectedTA || undefined,
        notes: notes || undefined,
        ...(selectedSalt ? { salt_name: selectedSalt } : {}),
      } as any);
      setRegisterResult(r.data);
    } catch (err: any) {
      setRegisterError(err.response?.data?.detail || 'Registration failed');
    } finally {
      setRegistering(false);
    }
  };

  const handleKetcherSave = (molfile: string, smilesOut: string) => {
    if (smilesOut) setSmiles(smilesOut);
    else if (molfile) setSmiles(molfile);
    setShowKetcher(false);
  };

  const handleReset = () => {
    setSmiles('');
    setStdResult(null);
    setStdError(null);
    setUniquenessResult(null);
    setUniquenessError(null);
    setRegisterResult(null);
    setRegisterError(null);
    setNotes('');
    setSelectedTA(undefined);
    setSelectedSalt('');
  };

  const canRegister = uniquenessResult != null;

  return (
    <div className="flex-1 overflow-auto p-6">
      <div className="max-w-3xl mx-auto space-y-6">
        {/* SMILES Input */}
        <div className="rounded-xl border p-5" style={{ borderColor: 'var(--card-border)', backgroundColor: 'var(--card-bg)' }}>
          <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>
            Structure Input
          </label>
          <textarea
            value={smiles}
            onChange={(e) => setSmiles(e.target.value)}
            placeholder="Paste SMILES string here, e.g. CC(=O)Oc1ccccc1C(=O)O"
            rows={3}
            className="w-full px-3 py-2 text-sm font-mono rounded-lg border transition-colors resize-none"
            style={{
              backgroundColor: 'var(--input-bg)',
              borderColor: 'var(--input-border)',
              color: 'var(--text-primary)',
            }}
          />
          <div className="flex items-center gap-2 mt-3">
            <button
              onClick={handleStandardise}
              disabled={!smiles.trim() || standardising}
              className="px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors disabled:opacity-50"
              style={{ backgroundColor: '#0D9488' }}
            >
              {standardising ? 'Standardising...' : 'Standardise'}
            </button>
            <button
              onClick={() => setShowKetcher(true)}
              className="px-4 py-2 text-sm font-medium rounded-lg border transition-colors"
              style={{ borderColor: 'var(--card-border)', color: 'var(--text-secondary)' }}
            >
              Draw in Ketcher
            </button>
            {(stdResult || stdError) && (
              <button
                onClick={handleReset}
                className="px-3 py-2 text-sm rounded-lg transition-colors"
                style={{ color: 'var(--text-muted)' }}
              >
                Reset
              </button>
            )}
          </div>
          {stdError && (
            <div className="mt-3 px-3 py-2 rounded-lg text-sm" style={{ backgroundColor: '#fef2f2', color: '#991b1b' }}>
              {stdError}
            </div>
          )}
        </div>

        {/* Standardisation Result */}
        {stdResult && (
          <div className="rounded-xl border p-5" style={{ borderColor: 'var(--card-border)', backgroundColor: 'var(--card-bg)' }}>
            <h3 className="text-sm font-medium mb-4" style={{ color: 'var(--text-primary)' }}>Standardisation Result</h3>

            {/* Structure image */}
            <div className="rounded-lg overflow-hidden flex items-center justify-center mb-4" style={{ height: 180, backgroundColor: 'var(--content-bg)' }}>
              {svgLoading ? (
                <div className="w-6 h-6 border-2 border-gray-300 border-t-teal-500 rounded-full animate-spin" />
              ) : structureSvg ? (
                <div dangerouslySetInnerHTML={{ __html: structureSvg }} className="w-full h-full flex items-center justify-center" />
              ) : (
                <span className="text-xs font-mono" style={{ color: 'var(--text-muted)' }}>No render available</span>
              )}
            </div>

            {/* Properties grid */}
            <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
              <div className="flex items-center justify-between">
                <span style={{ color: 'var(--text-secondary)' }}>Canonical SMILES</span>
                <div className="flex items-center">
                  <span className="font-mono text-xs truncate max-w-[200px]" title={stdResult.canonical_smiles} style={{ color: 'var(--text-primary)' }}>
                    {stdResult.canonical_smiles}
                  </span>
                  <CopyButton text={stdResult.canonical_smiles} />
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span style={{ color: 'var(--text-secondary)' }}>InChIKey</span>
                <div className="flex items-center">
                  <span className="font-mono text-xs" style={{ color: 'var(--text-primary)' }}>{stdResult.inchi_key}</span>
                  <CopyButton text={stdResult.inchi_key} />
                </div>
              </div>
              {stdResult.inchi && (
                <div className="col-span-2">
                  <div className="flex items-center justify-between">
                    <span style={{ color: 'var(--text-secondary)' }}>InChI</span>
                    <CopyButton text={stdResult.inchi} />
                  </div>
                  <div
                    className="font-mono text-xs mt-1 p-2 rounded-lg break-all cursor-pointer"
                    style={{ backgroundColor: 'var(--content-bg)', color: 'var(--text-primary)', border: '1px solid var(--card-border)', maxHeight: 60, overflow: 'hidden' }}
                    onClick={(e) => {
                      const el = e.currentTarget;
                      el.style.maxHeight = el.style.maxHeight === 'none' ? '60px' : 'none';
                    }}
                    title="Click to expand/collapse"
                  >
                    {stdResult.inchi}
                  </div>
                </div>
              )}
              <div className="flex items-center justify-between">
                <span style={{ color: 'var(--text-secondary)' }}>Molecular Formula</span>
                <span className="font-mono text-xs" style={{ color: 'var(--text-primary)' }}>{stdResult.mol_formula}</span>
              </div>
              <div className="flex items-center justify-between">
                <span style={{ color: 'var(--text-secondary)' }}>Molecular Weight</span>
                <span className="font-mono text-xs" style={{ color: 'var(--text-primary)' }}>{Math.round(stdResult.mol_weight * 10000) / 10000}</span>
              </div>
              <div className="flex items-center justify-between">
                <span style={{ color: 'var(--text-secondary)' }}>Exact Mass</span>
                <span className="font-mono text-xs" style={{ color: 'var(--text-primary)' }}>{Math.round(stdResult.exact_mass * 10000) / 10000}</span>
              </div>
              <div className="flex items-center justify-between">
                <span style={{ color: 'var(--text-secondary)' }}>Stereocenters</span>
                <span className="font-mono text-xs" style={{ color: 'var(--text-primary)' }}>
                  {stdResult.num_defined_stereocenters} defined / {stdResult.num_stereocenters - stdResult.num_defined_stereocenters} undefined
                </span>
              </div>
            </div>

            {/* Salts removed */}
            {stdResult.salts_removed && stdResult.salts_removed.length > 0 && (
              <div className="mt-3 pt-3 border-t" style={{ borderColor: 'var(--card-border)' }}>
                <span className="text-xs font-medium" style={{ color: 'var(--text-secondary)' }}>Salts Removed:</span>
                <div className="flex gap-2 mt-1">
                  {stdResult.salts_removed.map((salt, i) => (
                    <span key={i} className="text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: '#fef3c7', color: '#92400e' }}>
                      {salt.name} ({salt.smiles})
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Uniqueness check */}
            <div className="mt-4 pt-4 border-t" style={{ borderColor: 'var(--card-border)' }}>
              <button
                onClick={handleCheckUniqueness}
                disabled={checking}
                className="px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors disabled:opacity-50"
                style={{ backgroundColor: '#2563eb' }}
              >
                {checking ? 'Checking...' : 'Check Uniqueness'}
              </button>
              {uniquenessError && (
                <div className="mt-2 px-3 py-2 rounded-lg text-sm" style={{ backgroundColor: '#fef2f2', color: '#991b1b' }}>
                  {uniquenessError}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Uniqueness Result */}
        {uniquenessResult && (
          <div
            className="rounded-xl border p-5"
            style={{
              borderColor: uniquenessResult.is_unique ? '#bbf7d0' : '#fde68a',
              backgroundColor: uniquenessResult.is_unique ? '#f0fdf4' : '#fffbeb',
            }}
          >
            <div className="flex items-center gap-2 mb-2">
              {uniquenessResult.is_unique ? (
                <svg className="w-5 h-5" style={{ color: '#16a34a' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              ) : (
                <svg className="w-5 h-5" style={{ color: '#d97706' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              )}
              <span className="text-sm font-medium" style={{
                color: uniquenessResult.is_unique ? '#166534' : '#92400e',
              }}>
                {uniquenessResult.is_unique
                  ? 'Unique — new compound, ready to register'
                  : `Already registered as ${uniquenessResult.matched_parent_code}`}
              </span>
            </div>
          </div>
        )}

        {/* Registration form */}
        {canRegister && !registerResult && (
          <div className="rounded-xl border p-5" style={{ borderColor: 'var(--card-border)', backgroundColor: 'var(--card-bg)' }}>
            <h3 className="text-sm font-medium mb-4" style={{ color: 'var(--text-primary)' }}>Register Compound</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>Therapeutic Area</label>
                <select
                  value={selectedTA ?? ''}
                  onChange={(e) => setSelectedTA(e.target.value || undefined)}
                  className="w-full px-3 py-2 text-sm rounded-lg border transition-colors"
                  style={{ backgroundColor: 'var(--input-bg)', borderColor: 'var(--input-border)', color: 'var(--text-primary)' }}
                >
                  <option value="">-- Select --</option>
                  {therapeuticAreas.map(ta => (
                    <option key={ta.code} value={ta.code}>{ta.code} - {ta.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>Salt Form</label>
                <select
                  value={selectedSalt}
                  onChange={(e) => setSelectedSalt(e.target.value)}
                  className="w-full px-3 py-2 text-sm rounded-lg border transition-colors"
                  style={{ backgroundColor: 'var(--input-bg)', borderColor: 'var(--input-border)', color: 'var(--text-primary)' }}
                >
                  <option value="">Free base / No salt</option>
                  {salts.map(s => (
                    <option key={s.salt_id} value={s.name}>{s.name}{s.formula ? ` (${s.formula})` : ''}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>Notes</label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={2}
                  className="w-full px-3 py-2 text-sm rounded-lg border transition-colors resize-none"
                  style={{ backgroundColor: 'var(--input-bg)', borderColor: 'var(--input-border)', color: 'var(--text-primary)' }}
                  placeholder="Optional notes..."
                />
              </div>
              <button
                onClick={handleRegister}
                disabled={registering}
                className="px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors disabled:opacity-50"
                style={{ backgroundColor: '#0D9488' }}
              >
                {registering ? 'Registering...' : 'Register Compound'}
              </button>
              {registerError && (
                <div className="px-3 py-2 rounded-lg text-sm" style={{ backgroundColor: '#fef2f2', color: '#991b1b' }}>
                  {registerError}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Registration success */}
        {registerResult && (
          <div className="rounded-xl border p-5" style={{ borderColor: '#bbf7d0', backgroundColor: '#f0fdf4' }}>
            <div className="flex items-center gap-2 mb-3">
              <svg className="w-5 h-5" style={{ color: '#16a34a' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="text-sm font-medium" style={{ color: '#166534' }}>Compound Registered Successfully</span>
            </div>
            <div className="space-y-1 text-sm">
              <div><span style={{ color: '#166534' }}>Parent:</span> <span className="font-mono font-medium" style={{ color: '#0D9488' }}>{registerResult.parent_code}</span></div>
              <div><span style={{ color: '#166534' }}>Version:</span> <span className="font-mono" style={{ color: 'var(--text-primary)' }}>{registerResult.version_code}</span></div>
              <div><span style={{ color: '#166534' }}>Lot:</span> <span className="font-mono" style={{ color: 'var(--text-primary)' }}>{registerResult.lot_code}</span></div>
            </div>
            <button
              onClick={handleReset}
              className="mt-3 px-4 py-2 text-sm font-medium rounded-lg transition-colors"
              style={{ backgroundColor: '#0D9488', color: 'white' }}
            >
              Register Another
            </button>
          </div>
        )}
      </div>

      {/* Ketcher modal */}
      <StructureEditorModal
        isOpen={showKetcher}
        onClose={() => setShowKetcher(false)}
        onSave={handleKetcherSave}
        initialStructure={smiles || ''}
        title="Draw Structure"
      />
    </div>
  );
};

// Settings tab removed — rules now live in Admin > Registration Rules
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const _SettingsTabRemoved: React.FC = () => {
  const [rules, setRules] = useState<RegistrationRules | null>(null);
  const [rulesLoading, setRulesLoading] = useState(true);
  const [saveStatus, setSaveStatus] = useState<string | null>(null);
  const [salts, setSalts] = useState<Salt[]>([]);
  const [saltsLoading, setSaltsLoading] = useState(true);
  const [therapeuticAreas, setTherapeuticAreas] = useState<TherapeuticArea[]>([]);
  const [taLoading, setTaLoading] = useState(true);

  useEffect(() => {
    registrationAPI.getRules()
      .then(r => setRules(r.data))
      .catch(() => {})
      .finally(() => setRulesLoading(false));

    registrationAPI.getSaltDictionary()
      .then(r => setSalts(Array.isArray(r.data) ? r.data : []))
      .catch(() => {})
      .finally(() => setSaltsLoading(false));

    registrationAPI.getTherapeuticAreas()
      .then(r => setTherapeuticAreas(Array.isArray(r.data) ? r.data : []))
      .catch(() => {})
      .finally(() => setTaLoading(false));
  }, []);

  // Auto-save: update a single field immediately
  const updateRule = async (field: string, value: any) => {
    if (!rules) return;
    const prev = { ...rules };
    setRules({ ...rules, [field]: value }); // Optimistic update
    setSaveStatus('saving');
    try {
      const r = await registrationAPI.updateRules({ [field]: value } as any);
      // Sync with server response (authoritative)
      if (r.data && r.data.tenant_id) setRules(r.data);
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus(null), 1500);
    } catch {
      setRules(prev); // Revert on failure
      setSaveStatus('error');
      setTimeout(() => setSaveStatus(null), 2000);
    }
  };

  const Toggle: React.FC<{ field: string; label: string; desc: string }> = ({ field, label, desc }) => (
    <label className="flex items-start gap-3 cursor-pointer">
      <input
        type="checkbox"
        checked={!!(rules as any)?.[field]}
        onChange={(e) => updateRule(field, e.target.checked)}
        className="mt-0.5 rounded border-gray-300 text-teal-600 focus:ring-teal-500"
      />
      <div>
        <div className="text-sm" style={{ color: 'var(--text-primary)' }}>{label}</div>
        <div className="text-xs" style={{ color: 'var(--text-muted)' }}>{desc}</div>
      </div>
    </label>
  );

  const Dropdown: React.FC<{ field: string; label: string; desc: string; options: { value: string; label: string }[] }> = ({ field, label, desc, options }) => (
    <div>
      <div className="text-sm mb-1" style={{ color: 'var(--text-primary)' }}>{label}</div>
      <div className="text-xs mb-1.5" style={{ color: 'var(--text-muted)' }}>{desc}</div>
      <select
        value={(rules as any)?.[field] || options[0]?.value}
        onChange={(e) => updateRule(field, e.target.value)}
        className="px-3 py-1.5 text-sm rounded-lg border"
        style={{ backgroundColor: 'var(--input-bg)', borderColor: 'var(--input-border)', color: 'var(--text-primary)' }}
      >
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );

  return (
    <div className="flex-1 overflow-auto p-6">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Save indicator */}
        {saveStatus && (
          <div className="fixed top-4 right-4 z-50 px-3 py-1.5 rounded-lg text-xs font-medium shadow-lg" style={{
            backgroundColor: saveStatus === 'saved' ? '#f0fdf4' : saveStatus === 'error' ? '#fef2f2' : '#f8fafc',
            color: saveStatus === 'saved' ? '#16a34a' : saveStatus === 'error' ? '#dc2626' : '#64748b',
            border: `1px solid ${saveStatus === 'saved' ? '#bbf7d0' : saveStatus === 'error' ? '#fecaca' : '#e2e8f0'}`,
          }}>
            {saveStatus === 'saving' ? 'Saving...' : saveStatus === 'saved' ? 'Saved' : 'Save failed'}
          </div>
        )}

        {/* Standardisation Pipeline */}
        <div className="rounded-xl border p-5" style={{ borderColor: 'var(--card-border)', backgroundColor: 'var(--card-bg)' }}>
          <h3 className="text-sm font-medium mb-4" style={{ color: 'var(--text-primary)' }}>Standardisation Pipeline</h3>
          {rulesLoading ? (
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 border-2 border-gray-300 border-t-teal-500 rounded-full animate-spin" />
              <span className="text-xs" style={{ color: 'var(--text-muted)' }}>Loading...</span>
            </div>
          ) : rules ? (
            <div className="space-y-4">
              <Dropdown field="aromatise_policy" label="Aromaticity" desc="How to handle aromatic ring notation"
                options={[{ value: 'aromatise', label: 'Aromatise (default)' }, { value: 'kekulise', label: 'Kekulise' }, { value: 'preserve', label: 'Preserve as drawn' }]} />
              <Toggle field="normalise_groups" label="Normalise functional groups" desc="Standardise nitro, sulfo, and other functional group representations" />
              <Dropdown field="neutralise_policy" label="Charge handling" desc="How to handle charged species"
                options={[{ value: 'neutralise', label: 'Neutralise (default)' }, { value: 'reionise', label: 'Reionise' }, { value: 'preserve', label: 'Preserve charges' }]} />
              <Toggle field="disconnect_metals" label="Disconnect metals" desc="Break metal-ligand bonds for organometallic compounds" />
              <Dropdown field="salt_strip_policy" label="Salt stripping" desc="How to handle multi-component structures (salts, solvates)"
                options={[{ value: 'strip', label: 'Strip salts (default)' }, { value: 'keep', label: 'Keep full structure' }]} />
              <Dropdown field="tautomer_policy" label="Tautomer handling" desc="Whether to canonicalise tautomers (keto/enol, lactam/lactim)"
                options={[{ value: 'canonicalise', label: 'Canonicalise (default)' }, { value: 'preserve', label: 'Preserve as drawn' }]} />
              <Dropdown field="stereo_policy" label="Stereochemistry" desc="How to handle stereocentres and E/Z bonds"
                options={[{ value: 'preserve', label: 'Preserve (default)' }, { value: 'strip', label: 'Strip all stereo' }]} />
            </div>
          ) : null}
        </div>

        {/* Validation Rules */}
        <div className="rounded-xl border p-5" style={{ borderColor: 'var(--card-border)', backgroundColor: 'var(--card-bg)' }}>
          <h3 className="text-sm font-medium mb-4" style={{ color: 'var(--text-primary)' }}>Validation Rules</h3>
          {rules && (
            <div className="space-y-4">
              <div>
                <div className="text-sm mb-1" style={{ color: 'var(--text-primary)' }}>Max molecular weight</div>
                <div className="text-xs mb-1.5" style={{ color: 'var(--text-muted)' }}>Reject compounds above this weight (0 = no limit)</div>
                <input
                  type="number"
                  value={rules.mw_max ?? 0}
                  onChange={(e) => updateRule('mw_max', parseFloat(e.target.value) || 0)}
                  className="w-32 px-3 py-1.5 text-sm rounded-lg border"
                  style={{ backgroundColor: 'var(--input-bg)', borderColor: 'var(--input-border)', color: 'var(--text-primary)' }}
                />
              </div>
              <Toggle field="must_have_structure" label="Require structure" desc="Reject registrations without a valid chemical structure" />
              <Toggle field="reject_invalid_valence" label="Reject invalid valence" desc="Reject molecules with chemically invalid bond counts" />
            </div>
          )}
        </div>

        {/* Registration Policies */}
        <div className="rounded-xl border p-5" style={{ borderColor: 'var(--card-border)', backgroundColor: 'var(--card-bg)' }}>
          <h3 className="text-sm font-medium mb-4" style={{ color: 'var(--text-primary)' }}>Registration Policies</h3>
          {rules && (
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>Organisation Code</label>
                <input
                  type="text"
                  value={rules.org_code}
                  onBlur={(e) => updateRule('org_code', e.target.value)}
                  onChange={(e) => setRules({ ...rules, org_code: e.target.value })}
                  className="w-48 px-3 py-1.5 text-sm font-mono rounded-lg border"
                  style={{ backgroundColor: 'var(--input-bg)', borderColor: 'var(--input-border)', color: 'var(--text-primary)' }}
                />
              </div>
              <Toggle field="stereo_matters" label="Stereo creates new compound" desc="R/S and E/Z isomers are treated as different parent compounds" />
              <Toggle field="salt_creates_version" label="Salt creates new version" desc="Different salt forms create new versions under the same parent" />
              <Toggle field="auto_register" label="Auto-register on upload" desc="Automatically register compounds during file upload" />
              <Dropdown field="promote_role" label="Minimum role to promote" desc="Who can promote compounds to development"
                options={[{ value: 'member', label: 'Any member' }, { value: 'admin', label: 'Administrator (default)' }, { value: 'super_admin', label: 'Super Admin' }]} />
            </div>
          )}
        </div>

        {/* Therapeutic Areas */}
        <div className="rounded-xl border p-5" style={{ borderColor: 'var(--card-border)', backgroundColor: 'var(--card-bg)' }}>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
              Therapeutic Areas
              <span className="ml-2 text-xs font-normal" style={{ color: 'var(--text-muted)' }}>({therapeuticAreas.length} entries)</span>
            </h3>
          </div>
          {taLoading ? (
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 border-2 border-gray-300 border-t-teal-500 rounded-full animate-spin" />
              <span className="text-xs" style={{ color: 'var(--text-muted)' }}>Loading...</span>
            </div>
          ) : (
            <div className="overflow-auto max-h-64 rounded-lg border" style={{ borderColor: 'var(--card-border)' }}>
              <table className="w-full text-sm">
                <thead>
                  <tr style={{ backgroundColor: 'var(--hover-bg)' }}>
                    <th className="text-left px-3 py-1.5 text-xs font-medium sticky top-0" style={{ color: 'var(--text-secondary)', backgroundColor: 'var(--hover-bg)' }}>Code</th>
                    <th className="text-left px-3 py-1.5 text-xs font-medium sticky top-0" style={{ color: 'var(--text-secondary)', backgroundColor: 'var(--hover-bg)' }}>Name</th>
                    <th className="text-left px-3 py-1.5 text-xs font-medium sticky top-0" style={{ color: 'var(--text-secondary)', backgroundColor: 'var(--hover-bg)' }}>Description</th>
                  </tr>
                </thead>
                <tbody>
                  {therapeuticAreas.map(ta => (
                    <tr key={ta.code} className="border-t" style={{ borderColor: 'var(--card-border)' }}>
                      <td className="px-3 py-1.5 text-xs font-mono font-medium" style={{ color: '#0D9488' }}>{ta.code}</td>
                      <td className="px-3 py-1.5 text-xs" style={{ color: 'var(--text-primary)' }}>{ta.name}</td>
                      <td className="px-3 py-1.5 text-xs" style={{ color: 'var(--text-secondary)' }}>{ta.description || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// =====================================================================
// Audit Tab
// =====================================================================

const ACTION_LABELS: Record<string, { label: string; color: string }> = {
  register_parent:    { label: 'Registered',         color: '#16a34a' },
  add_version:        { label: 'Version Added',      color: '#2563eb' },
  add_lot:            { label: 'Lot Added',           color: '#7c3aed' },
  select_candidate:   { label: 'Candidate Selected',  color: '#d97706' },
  update_status:      { label: 'Status Updated',      color: '#0891b2' },
};

const AuditTab: React.FC = () => {
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    registrationAPI.getAudit()
      .then(r => setEntries(Array.isArray(r.data) ? r.data : []))
      .catch(err => setError(err.response?.data?.detail || 'Failed to load audit log'))
      .finally(() => setLoading(false));
  }, []);

  const formatDetails = (entry: AuditEntry): string => {
    if (!entry.details) return '-';
    const parts: string[] = [];
    if (entry.details.mol_formula) parts.push(entry.details.mol_formula);
    if (entry.details.therapeutic_area) parts.push(`TA: ${entry.details.therapeutic_area}`);
    if (entry.details.parent_code) parts.push(entry.details.parent_code);
    if (entry.details.salt_name) parts.push(`Salt: ${entry.details.salt_name}`);
    if (entry.details.notes) parts.push(entry.details.notes);
    return parts.length > 0 ? parts.join(' · ') : '-';
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-auto">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-6 h-6 border-2 border-gray-300 border-t-teal-500 rounded-full animate-spin" />
          </div>
        ) : error ? (
          <div className="flex items-center justify-center py-20">
            <span className="text-sm" style={{ color: '#ef4444' }}>{error}</span>
          </div>
        ) : entries.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 gap-2">
            <svg className="w-12 h-12" style={{ color: 'var(--text-muted)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <span className="text-sm" style={{ color: 'var(--text-muted)' }}>No audit entries yet</span>
          </div>
        ) : (
          <table className="w-full text-sm" style={{ color: 'var(--text-primary)' }}>
            <thead>
              <tr className="border-b" style={{ borderColor: 'var(--card-border)', backgroundColor: 'var(--hover-bg)' }}>
                <th className="text-left px-4 py-2 text-xs font-medium" style={{ color: 'var(--text-secondary)' }}>Date / Time</th>
                <th className="text-left px-4 py-2 text-xs font-medium" style={{ color: 'var(--text-secondary)' }}>User</th>
                <th className="text-left px-4 py-2 text-xs font-medium" style={{ color: 'var(--text-secondary)' }}>Action</th>
                <th className="text-left px-4 py-2 text-xs font-medium" style={{ color: 'var(--text-secondary)' }}>Compound Code</th>
                <th className="text-left px-4 py-2 text-xs font-medium" style={{ color: 'var(--text-secondary)' }}>Structure</th>
                <th className="text-left px-4 py-2 text-xs font-medium" style={{ color: 'var(--text-secondary)' }}>Details</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((e) => {
                const actionInfo = ACTION_LABELS[e.action] || { label: e.action, color: 'var(--text-secondary)' };
                return (
                  <tr
                    key={e.audit_id}
                    className="border-b transition-colors"
                    style={{ borderColor: 'var(--card-border)' }}
                    onMouseEnter={(ev) => { ev.currentTarget.style.backgroundColor = 'var(--hover-bg)'; }}
                    onMouseLeave={(ev) => { ev.currentTarget.style.backgroundColor = 'transparent'; }}
                  >
                    <td className="px-4 py-2 text-xs whitespace-nowrap" style={{ color: 'var(--text-secondary)' }}>
                      {formatDateTime(e.performed_at)}
                    </td>
                    <td className="px-4 py-2 text-xs" style={{ color: 'var(--text-primary)' }}>
                      {e.performed_by_name || 'System'}
                    </td>
                    <td className="px-4 py-2">
                      <span
                        className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
                        style={{ backgroundColor: `${actionInfo.color}18`, color: actionInfo.color, border: `1px solid ${actionInfo.color}40` }}
                      >
                        {actionInfo.label}
                      </span>
                    </td>
                    <td className="px-4 py-2 font-mono text-xs" style={{ color: '#0D9488' }}>
                      {e.entity_code || '-'}
                    </td>
                    <td className="px-4 py-1">
                      {e.canonical_smiles ? <StructureThumbnail smiles={e.canonical_smiles} /> : <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>-</span>}
                    </td>
                    <td className="px-4 py-2 text-xs max-w-[250px] truncate" title={formatDetails(e)} style={{ color: 'var(--text-muted)' }}>
                      {formatDetails(e)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

// =====================================================================
// Main RegistryPage
// =====================================================================

const TABS: { id: RegistryTab; label: string }[] = [
  { id: 'compounds', label: 'Compounds' },
  { id: 'versions', label: 'Versions' },
  { id: 'lots', label: 'Lots' },
  { id: 'salts', label: 'Salts' },
  { id: 'therapeutic-areas', label: 'Therapeutic Areas' },
  { id: 'register', label: 'Register' },
  { id: 'audit', label: 'Audit Log' },
];

// =====================================================================
// Versions Tab
// =====================================================================

const VersionsTab: React.FC = () => {
  const [compounds, setCompounds] = useState<RegisteredCompound[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    registrationAPI.getCompounds({ limit: 200 })
      .then(async (r) => {
        const items = Array.isArray(r.data) ? r.data : [];
        // Fetch versions for each compound
        const expanded = await Promise.all(
          items.slice(0, 50).map(async (c) => {
            try {
              const detail = await registrationAPI.getCompound(c.parent_code);
              return detail.data;
            } catch { return c; }
          })
        );
        setCompounds(expanded);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex items-center justify-center py-20"><div className="w-6 h-6 border-2 border-gray-300 border-t-teal-500 rounded-full animate-spin" /></div>;

  const allVersions = compounds.flatMap(c =>
    (c.versions || []).map(v => ({ ...v, parent_code: c.parent_code }))
  );

  return (
    <div className="overflow-auto">
      <table className="w-full text-sm" style={{ color: 'var(--text-primary)' }}>
        <thead><tr className="border-b" style={{ borderColor: 'var(--card-border)', backgroundColor: 'var(--hover-bg)' }}>
          <th className="text-left px-4 py-2 text-xs font-medium" style={{ color: 'var(--text-secondary)' }}>Version Code</th>
          <th className="text-left px-4 py-2 text-xs font-medium" style={{ color: 'var(--text-secondary)' }}>Parent Code</th>
          <th className="text-left px-4 py-2 text-xs font-medium" style={{ color: 'var(--text-secondary)' }}>Salt</th>
          <th className="text-left px-4 py-2 text-xs font-medium" style={{ color: 'var(--text-secondary)' }}>Candidate</th>
          <th className="text-left px-4 py-2 text-xs font-medium" style={{ color: 'var(--text-secondary)' }}>Lots</th>
          <th className="text-left px-4 py-2 text-xs font-medium" style={{ color: 'var(--text-secondary)' }}>Created</th>
        </tr></thead>
        <tbody>
          {allVersions.length === 0 ? (
            <tr><td colSpan={6} className="text-center py-12 text-sm" style={{ color: 'var(--text-muted)' }}>No versions found</td></tr>
          ) : allVersions.map(v => (
            <tr key={v.version_code} className="border-b" style={{ borderColor: 'var(--card-border)' }}>
              <td className="px-4 py-2 font-mono text-xs" style={{ color: '#0D9488' }}>{v.version_code}</td>
              <td className="px-4 py-2 font-mono text-xs" style={{ color: 'var(--text-primary)' }}>{v.parent_code}</td>
              <td className="px-4 py-2 text-xs">{v.salt_name || '-'}</td>
              <td className="px-4 py-2 text-xs">{v.is_candidate ? <span style={{ color: '#2563eb' }}>Yes</span> : '-'}</td>
              <td className="px-4 py-2 text-xs font-mono">{v.lots?.length || 0}</td>
              <td className="px-4 py-2 text-xs" style={{ color: 'var(--text-secondary)' }}>{formatDate(v.created_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// =====================================================================
// Lots Tab
// =====================================================================

const LotsTab: React.FC = () => {
  const [compounds, setCompounds] = useState<RegisteredCompound[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    registrationAPI.getCompounds({ limit: 200 })
      .then(async (r) => {
        const items = Array.isArray(r.data) ? r.data : [];
        const expanded = await Promise.all(
          items.slice(0, 50).map(async (c) => {
            try { return (await registrationAPI.getCompound(c.parent_code)).data; } catch { return c; }
          })
        );
        setCompounds(expanded);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex items-center justify-center py-20"><div className="w-6 h-6 border-2 border-gray-300 border-t-teal-500 rounded-full animate-spin" /></div>;

  const allLots = compounds.flatMap(c =>
    (c.versions || []).flatMap(v =>
      v.lots.map(l => ({ ...l, version_code: v.version_code, parent_code: c.parent_code }))
    )
  );

  return (
    <div className="overflow-auto">
      <table className="w-full text-sm" style={{ color: 'var(--text-primary)' }}>
        <thead><tr className="border-b" style={{ borderColor: 'var(--card-border)', backgroundColor: 'var(--hover-bg)' }}>
          <th className="text-left px-4 py-2 text-xs font-medium" style={{ color: 'var(--text-secondary)' }}>Lot Code</th>
          <th className="text-left px-4 py-2 text-xs font-medium" style={{ color: 'var(--text-secondary)' }}>Version Code</th>
          <th className="text-left px-4 py-2 text-xs font-medium" style={{ color: 'var(--text-secondary)' }}>Batch</th>
          <th className="text-left px-4 py-2 text-xs font-medium" style={{ color: 'var(--text-secondary)' }}>Purity</th>
          <th className="text-left px-4 py-2 text-xs font-medium" style={{ color: 'var(--text-secondary)' }}>Supplier</th>
          <th className="text-left px-4 py-2 text-xs font-medium" style={{ color: 'var(--text-secondary)' }}>Created</th>
        </tr></thead>
        <tbody>
          {allLots.length === 0 ? (
            <tr><td colSpan={6} className="text-center py-12 text-sm" style={{ color: 'var(--text-muted)' }}>No lots found</td></tr>
          ) : allLots.map(l => (
            <tr key={l.lot_code} className="border-b" style={{ borderColor: 'var(--card-border)' }}>
              <td className="px-4 py-2 font-mono text-xs" style={{ color: '#0D9488' }}>{l.lot_code}</td>
              <td className="px-4 py-2 font-mono text-xs" style={{ color: 'var(--text-primary)' }}>{l.version_code}</td>
              <td className="px-4 py-2 text-xs">{l.batch_number || '-'}</td>
              <td className="px-4 py-2 text-xs font-mono">{l.purity_percent != null ? `${l.purity_percent}%` : '-'}</td>
              <td className="px-4 py-2 text-xs">{l.supplier || '-'}</td>
              <td className="px-4 py-2 text-xs" style={{ color: 'var(--text-secondary)' }}>{formatDate(l.created_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// =====================================================================
// Salts Tab
// =====================================================================

const SaltsTab: React.FC = () => {
  const [salts, setSalts] = useState<Salt[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    registrationAPI.getSaltDictionary()
      .then(r => setSalts(Array.isArray(r.data) ? r.data : []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex items-center justify-center py-20"><div className="w-6 h-6 border-2 border-gray-300 border-t-teal-500 rounded-full animate-spin" /></div>;

  return (
    <div className="overflow-auto">
      <table className="w-full text-sm" style={{ color: 'var(--text-primary)' }}>
        <thead><tr className="border-b" style={{ borderColor: 'var(--card-border)', backgroundColor: 'var(--hover-bg)' }}>
          <th className="text-left px-4 py-2 text-xs font-medium" style={{ color: 'var(--text-secondary)' }}>Name</th>
          <th className="text-left px-4 py-2 text-xs font-medium" style={{ color: 'var(--text-secondary)' }}>SMILES</th>
          <th className="text-left px-4 py-2 text-xs font-medium" style={{ color: 'var(--text-secondary)' }}>Formula</th>
          <th className="text-right px-4 py-2 text-xs font-medium" style={{ color: 'var(--text-secondary)' }}>Charge</th>
          <th className="text-left px-4 py-2 text-xs font-medium" style={{ color: 'var(--text-secondary)' }}>Category</th>
          <th className="text-center px-4 py-2 text-xs font-medium" style={{ color: 'var(--text-secondary)' }}>Enabled</th>
        </tr></thead>
        <tbody>
          {salts.map(s => (
            <tr key={s.salt_id} className="border-b" style={{ borderColor: 'var(--card-border)', opacity: s.enabled ? 1 : 0.5 }}>
              <td className="px-4 py-2 text-xs font-medium" style={{ color: 'var(--text-primary)' }}>{s.name}</td>
              <td className="px-4 py-2 font-mono text-xs" style={{ color: 'var(--text-secondary)' }}>{s.smiles}</td>
              <td className="px-4 py-2 text-xs">{s.formula || '-'}</td>
              <td className="px-4 py-2 text-xs text-right font-mono">{s.charge ?? '-'}</td>
              <td className="px-4 py-2 text-xs">{s.category || '-'}</td>
              <td className="px-4 py-2 text-center">
                <span className="inline-block w-2 h-2 rounded-full" style={{ background: s.enabled ? '#10b981' : '#d1d5db' }} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// =====================================================================
// Therapeutic Areas Tab
// =====================================================================

const TherapeuticAreasTab: React.FC = () => {
  const [areas, setAreas] = useState<TherapeuticArea[]>([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<RegistrationStats | null>(null);

  useEffect(() => {
    Promise.all([
      registrationAPI.getTherapeuticAreas(),
      registrationAPI.getStats(),
    ]).then(([taR, statsR]) => {
      setAreas(Array.isArray(taR.data) ? taR.data : []);
      setStats(statsR.data);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex items-center justify-center py-20"><div className="w-6 h-6 border-2 border-gray-300 border-t-teal-500 rounded-full animate-spin" /></div>;

  return (
    <div className="overflow-auto">
      <table className="w-full text-sm" style={{ color: 'var(--text-primary)' }}>
        <thead><tr className="border-b" style={{ borderColor: 'var(--card-border)', backgroundColor: 'var(--hover-bg)' }}>
          <th className="text-left px-4 py-2 text-xs font-medium" style={{ color: 'var(--text-secondary)' }}>Code</th>
          <th className="text-left px-4 py-2 text-xs font-medium" style={{ color: 'var(--text-secondary)' }}>Name</th>
          <th className="text-left px-4 py-2 text-xs font-medium" style={{ color: 'var(--text-secondary)' }}>Description</th>
          <th className="text-right px-4 py-2 text-xs font-medium" style={{ color: 'var(--text-secondary)' }}>Compounds</th>
        </tr></thead>
        <tbody>
          {areas.map(ta => (
            <tr key={ta.code} className="border-b" style={{ borderColor: 'var(--card-border)' }}>
              <td className="px-4 py-2 font-mono text-xs font-medium" style={{ color: '#0D9488' }}>{ta.code}</td>
              <td className="px-4 py-2 text-xs" style={{ color: 'var(--text-primary)' }}>{ta.name}</td>
              <td className="px-4 py-2 text-xs" style={{ color: 'var(--text-secondary)' }}>{ta.description || '-'}</td>
              <td className="px-4 py-2 text-xs text-right font-mono" style={{ color: 'var(--text-primary)' }}>
                {stats?.by_therapeutic_area?.[ta.code] || 0}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const RegistryPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<RegistryTab>('compounds');
  const { branding } = useAuth();
  const orgName = branding.name && branding.name !== 'Adroit' ? branding.name : null;

  return (
    <div className="flex flex-col h-full">
      <PageHeader
        title="Registry"
        subtitle={orgName ? `Registered to: ${orgName}` : 'Compound registration, tracking, and management'}
        breadcrumbs={[{ label: 'Chemistry', to: '/chemistry/files' }, { label: 'Registry' }]}
      />

      {/* Tab bar */}
      <div
        className="flex-shrink-0 border-b px-4 flex gap-0.5"
        style={{ borderColor: 'var(--card-border)', backgroundColor: 'var(--card-bg)' }}
      >
        {TABS.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className="px-4 py-2.5 text-sm whitespace-nowrap transition-colors"
            style={{
              color: activeTab === tab.id ? '#0D9488' : 'var(--text-secondary)',
              borderBottom: activeTab === tab.id ? '2px solid #0D9488' : '2px solid transparent',
              fontWeight: activeTab === tab.id ? 600 : 400,
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div className="flex-1 overflow-auto" style={{ backgroundColor: 'var(--content-bg)' }}>
        {activeTab === 'compounds' && <CompoundsTab />}
        {activeTab === 'versions' && <VersionsTab />}
        {activeTab === 'lots' && <LotsTab />}
        {activeTab === 'salts' && <SaltsTab />}
        {activeTab === 'therapeutic-areas' && <TherapeuticAreasTab />}
        {activeTab === 'register' && <RegisterTab />}
        {activeTab === 'audit' && <AuditTab />}
      </div>
    </div>
  );
};

export default RegistryPage;
